import { useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CodeIcon from "@mui/icons-material/Code";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import ChatMessage from "./ChatMessage";
import classes from "./HTMLReportViewer.module.css";

const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(",");

function FileChip({ file, onRemove }) {
  const isImage = file.type.startsWith("image/");
  return (
    <Chip
      icon={
        isImage ? (
          <ImageOutlinedIcon fontSize="small" />
        ) : (
          <InsertDriveFileOutlinedIcon fontSize="small" />
        )
      }
      label={file.name.length > 20 ? file.name.slice(0, 17) + "…" : file.name}
      onDelete={onRemove}
      size="small"
      className={classes.fileChip}
      title={file.name}
    />
  );
}

let msgIdCounter = 0;
function newMsgId() {
  return `msg-${++msgIdCounter}`;
}

export default function HTMLReportViewer({
  html: originalHtml,
  jobId,
  transactionId,
  userId,
  onBack,
  onSaveUpdate,
  onSaveNew,
}) {
  const fileInputRef = useRef(null);
  const splitContainerRef = useRef(null);
  const aiSplitContainerRef = useRef(null);
  const draggingRef = useRef(false);
  const mainIframeRef = useRef(null);
  const shownHtmlRef = useRef(null);
  const manualHtmlRef = useRef(null);
  const chatBottomRef = useRef(null);

  const [splitRatio, setSplitRatio] = useState(50);
  const [aiSplitRatio, setAiSplitRatio] = useState(55);
  const [isDragging, setIsDragging] = useState(false);

  const [displayHtml, setDisplayHtml] = useState(originalHtml);

  // Edit mode: null | "ai" | "manual"
  const [editMode, setEditMode] = useState(null);
  const [editMenuAnchor, setEditMenuAnchor] = useState(null);

  // ── AI chat state ──────────────────────────────────────────────────────────
  const [chatHistory, setChatHistory] = useState([]);
  const [promptText, setPromptText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [streaming, setStreaming] = useState(false);

  // ── Preview state (set after AI applies changes) ───────────────────────────
  const [previewHtml, setPreviewHtml] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  // Counter-based iframe key — increments on every content change so React
  // always recreates the iframe, even when the HTML length doesn't change
  const [iframeVersion, setIframeVersion] = useState(0);

  // ── Main iframe visibility toggle ──────────────────────────────────────────
  const [iframeVisible, setIframeVisible] = useState(true);

  // ── Manual flow ────────────────────────────────────────────────────────────
  const [manualHtml, setManualHtml] = useState("");

  // ── Shared save dialog ─────────────────────────────────────────────────────
  const [saveDialog, setSaveDialog] = useState(false);
  const [pendingSaveHtml, setPendingSaveHtml] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Auto-scroll chat to bottom ─────────────────────────────────────────────
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  useEffect(() => {
    if (isPreviewing) setIframeVisible(true);
  }, [isPreviewing]);

  // ── Edit mode helpers ──────────────────────────────────────────────────────
  const openEditMenu = (e) => setEditMenuAnchor(e.currentTarget);
  const closeEditMenu = () => setEditMenuAnchor(null);

  const enterAIMode = () => {
    closeEditMenu();
    setEditMode("ai");
  };

  const enterManualMode = () => {
    closeEditMenu();
    setManualHtml(displayHtml);
    setEditMode("manual");
  };

  const cancelEdit = () => {
    setEditMode(null);
    setIsPreviewing(false);
    setPreviewHtml(null);
    setPromptText("");
    setAttachedFiles([]);
    setChatHistory([]);
  };

  // ── File handling ──────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...files.filter((f) => !existing.has(f.name + f.size))];
    });
    e.target.value = "";
  };

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Build conversation history for API (text-only, last 8 turns) ───────────
  const buildConversationHistory = useCallback((history) => {
    return history
      .filter((m) => m.role === "user" || (m.role === "ai" && m.type === "done"))
      .slice(-16)
      .map((m) => {
        if (m.role === "user") return { role: "user", content: m.text };
        return { role: "assistant", content: m.text };
      });
  }, []);

  // ── Streaming send ─────────────────────────────────────────────────────────
  const handleSend = async () => {
    if ((!promptText.trim() && attachedFiles.length === 0) || streaming) return;

    const userText = promptText.trim();
    const thinkingMsgId = newMsgId();

    // Capture current HTML and files before any state updates
    const currentHtml = isPreviewing ? previewHtml : displayHtml;
    const capturedFiles = [...attachedFiles];
    const history = buildConversationHistory(chatHistory);

    setChatHistory((prev) => [
      ...prev,
      { id: newMsgId(), role: "user", text: userText },
      { id: thinkingMsgId, role: "ai", type: "thinking", chunkCount: 0 },
    ]);
    setPromptText("");
    setAttachedFiles([]);
    setStreaming(true);

    try {
      const formData = new FormData();
      formData.append("promptText", userText);
      formData.append("existingHtml", currentHtml);
      formData.append("conversationHistory", JSON.stringify(history));
      capturedFiles.forEach((f) => formData.append("docs", f));

      // Use native fetch for true SSE streaming (axios buffers the full response)
      // Apply same auth headers as the api interceptor
      const token = localStorage.getItem("token");
      const fetchHeaders = {};
      if (token) {
        fetchHeaders["Authorization"] = `Bearer ${token}`;
        fetchHeaders["Language"] = window.selectedLanguage || "en";
      }

      const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8180/api";

      const response = await fetch(
        `${baseUrl}/llm-reporting/modify-html-report-stream`,
        { method: "POST", headers: fetchHeaders, body: formData }
      );

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        throw new Error(`Server error ${response.status}${errText ? `: ${errText}` : ""}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let chunkCount = 0;
      let handled = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const lines = buf.split(/\r?\n/);
        buf = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          let event;
          try { event = JSON.parse(raw); } catch (_) { continue; }

          if (event.type === "chunk") {
            chunkCount++;
            // Update thinking message with live progress counter
            setChatHistory((prev) =>
              prev.map((m) =>
                m.id === thinkingMsgId ? { ...m, chunkCount } : m
              )
            );
          }

          if (event.type === "error") throw new Error(event.error || "Stream error");

          if (event.type === "done" && !handled) {
            handled = true;
            setPreviewHtml(event.html);
            setIsPreviewing(true);
            setIframeVersion((v) => v + 1);
            const summary = event.summary || "Done! Review the preview, then save when ready.";
            setChatHistory((prev) =>
              prev
                .filter((m) => m.id !== thinkingMsgId)
                .concat({
                  id: newMsgId(),
                  role: "ai",
                  type: "done",
                  text: summary,
                  failures: event.failures || [],
                })
            );
          }
        }
      }

      if (!handled) {
        setChatHistory((prev) => prev.filter((m) => m.id !== thinkingMsgId));
      }
    } catch (err) {
      const msg = err.message || "Failed to process request";
      setChatHistory((prev) =>
        prev
          .filter((m) => m.id !== thinkingMsgId)
          .concat({ id: newMsgId(), role: "ai", type: "error", text: msg })
      );
      toast.error(msg);
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── AI preview banner actions ──────────────────────────────────────────────
  const handleAcceptPreview = () => {
    setPendingSaveHtml(previewHtml);
    setSaveDialog(true);
  };

  const handleDiscardPreview = () => {
    setPreviewHtml(null);
    setIsPreviewing(false);
  };

  // ── Manual save ────────────────────────────────────────────────────────────
  const handleManualSave = () => {
    setPendingSaveHtml(manualHtml);
    setSaveDialog(true);
  };

  // ── Shared save dialog ─────────────────────────────────────────────────────
  const handleSaveUpdateConfirm = async () => {
    setSaving(true);
    try {
      await onSaveUpdate(pendingSaveHtml);
      setDisplayHtml(pendingSaveHtml);
      setSaveDialog(false);
      setEditMode(null);
      setIsPreviewing(false);
      setPreviewHtml(null);
      setChatHistory([]);
      toast.success("Report updated successfully.");
    } catch {
      toast.error("Failed to update report.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNewConfirm = async () => {
    setSaving(true);
    try {
      await onSaveNew(pendingSaveHtml);
      setDisplayHtml(pendingSaveHtml);
      setSaveDialog(false);
      setEditMode(null);
      setIsPreviewing(false);
      setPreviewHtml(null);
      setChatHistory([]);
      toast.success("Saved as a new report entry.");
    } catch {
      toast.error("Failed to save new report.");
    } finally {
      setSaving(false);
    }
  };

  // ── Resizable dividers ─────────────────────────────────────────────────────
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);

    const onMouseMove = (ev) => {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const ratio = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitRatio(Math.min(Math.max(ratio, 20), 80));
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      setIsDragging(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const handleAiDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);

    const onMouseMove = (ev) => {
      if (!draggingRef.current || !aiSplitContainerRef.current) return;
      const rect = aiSplitContainerRef.current.getBoundingClientRect();
      const ratio = ((ev.clientX - rect.left) / rect.width) * 100;
      setAiSplitRatio(Math.min(Math.max(ratio, 25), 75));
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      setIsDragging(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  // ── Render helpers ─────────────────────────────────────────────────────────
  const shownHtml = isPreviewing ? previewHtml : displayHtml;
  shownHtmlRef.current = shownHtml;
  manualHtmlRef.current = manualHtml;
  const hasPromptContent = promptText.trim() || attachedFiles.length > 0;
  const showChatPanel = editMode === "ai";

  // ── Print intercept ────────────────────────────────────────────────────────
  const handleMainIframeLoad = useCallback(() => {
    const iframe = mainIframeRef.current;
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.print = () => {
        window.postMessage({ type: "__iframe_print_request__" }, "*");
      };
    } catch (_) { }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== "__iframe_print_request__") return;
      const html = e.data?.manual ? manualHtmlRef.current : shownHtmlRef.current;
      if (!html) return;
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.addEventListener("afterprint", () => win.close());
        setTimeout(() => win.close(), 3000);
      }, 400);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box className={classes.root}>
      {/* ── Top bar ── */}
      <Box className={classes.topBar}>
        <Tooltip title="Back to history">
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Typography className={classes.viewerTitle}>
          {isPreviewing
            ? "Preview — AI Modified Report"
            : editMode === "manual"
              ? "Manual HTML Editor"
              : "HTML Report Viewer"}
        </Typography>

        <Box className={classes.topActions}>
          {/* Normal mode: Edit Report dropdown */}
          {editMode === null && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DriveFileRenameOutlineIcon fontSize="small" />}
                endIcon={<ExpandMoreIcon fontSize="small" />}
                onClick={openEditMenu}
                className={classes.editBtn}
              >
                Edit Report
              </Button>
              <Menu
                anchorEl={editMenuAnchor}
                open={Boolean(editMenuAnchor)}
                onClose={closeEditMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ className: classes.editMenu }}
              >
                <MenuItem onClick={enterAIMode} className={classes.editMenuItem}>
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" sx={{ color: "#7c3aed" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Edit with AI"
                    secondary="Describe changes — AI applies them instantly"
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </MenuItem>
                <MenuItem onClick={enterManualMode} className={classes.editMenuItem}>
                  <ListItemIcon>
                    <CodeIcon fontSize="small" sx={{ color: "#0891b2" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Edit Manually"
                    secondary="Edit HTML source with live preview"
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}

          {/* AI mode: Cancel */}
          {editMode === "ai" && !isPreviewing && (
            <Button
              size="small"
              onClick={cancelEdit}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Cancel
            </Button>
          )}

          {/* AI preview mode: Discard + Apply Changes */}
          {editMode === "ai" && isPreviewing && (
            <>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleDiscardPreview}
                startIcon={<CloseIcon fontSize="small" />}
                disabled={streaming}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAcceptPreview}
                startIcon={<SaveOutlinedIcon fontSize="small" />}
                disabled={streaming}
              >
                Apply Changes
              </Button>
            </>
          )}

          {/* Manual mode: Cancel */}
          {editMode === "manual" && (
            <Button
              size="small"
              onClick={cancelEdit}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {/* ── AI preview banner ── */}
      {isPreviewing && (
        <Box className={classes.previewBanner}>
          <VisibilityOutlinedIcon fontSize="small" />
          <span>
            This is a <strong>preview</strong> of the AI-modified report. Click{" "}
            <strong>Apply Changes</strong> to save, or <strong>Discard</strong> to
            revert. You can also keep refining with more prompts.
          </span>
        </Box>
      )}

      {/* ── Manual mode: split editor + preview ── */}
      {editMode === "manual" ? (
        <>
          <Box
            className={classes.splitView}
            ref={splitContainerRef}
            style={{
              userSelect: isDragging ? "none" : undefined,
              cursor: isDragging ? "col-resize" : undefined,
            }}
          >
            <Box className={classes.previewPane} style={{ width: `${splitRatio}%` }}>
              <Box className={classes.paneHeader}>
                <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />
                <span>Live Preview</span>
              </Box>
              <iframe
                className={classes.reportIframe}
                srcDoc={manualHtml}
                title="Manual Preview"
                sandbox="allow-same-origin allow-scripts"
                onLoad={(e) => {
                  try {
                    if (e.target?.contentWindow) {
                      e.target.contentWindow.print = () =>
                        window.postMessage(
                          { type: "__iframe_print_request__", manual: true },
                          "*"
                        );
                    }
                  } catch (_) { }
                }}
              />
            </Box>

            <Box
              className={`${classes.splitDivider}${isDragging ? ` ${classes.splitDividerActive}` : ""}`}
              onMouseDown={handleDividerMouseDown}
            />

            <Box
              className={classes.editorPane}
              style={{ width: `${100 - splitRatio}%` }}
            >
              <Box className={classes.paneHeader}>
                <CodeIcon sx={{ fontSize: 13 }} />
                <span>HTML Source</span>
              </Box>
              <textarea
                className={classes.editorTextarea}
                value={manualHtml}
                onChange={(e) => setManualHtml(e.target.value)}
                spellCheck={false}
              />
            </Box>
          </Box>

          <Box className={classes.manualToolbar}>
            <Typography className={classes.inputHint} sx={{ flex: 1 }}>
              Edit HTML on the right — preview updates live on the left.
            </Typography>
            <Button size="small" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveOutlinedIcon fontSize="small" />}
              onClick={handleManualSave}
            >
              Save Changes
            </Button>
          </Box>
        </>
      ) : showChatPanel ? (
        /* ── AI mode: horizontal split (report left, chat right) ── */
        <Box
          className={classes.aiSplitView}
          ref={aiSplitContainerRef}
          style={{
            userSelect: isDragging ? "none" : undefined,
            cursor: isDragging ? "col-resize" : undefined,
          }}
        >
          {/* ── Report pane (left) ── */}
          <Box
            className={iframeVisible ? classes.aiReportPane : classes.aiReportPaneCollapsed}
            style={iframeVisible ? { width: `${aiSplitRatio}%` } : undefined}
          >
            <Box className={classes.iframeHeader}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {isPreviewing ? (
                  <AutoAwesomeIcon sx={{ fontSize: 13, color: "#7c3aed" }} />
                ) : (
                  <VisibilityOutlinedIcon sx={{ fontSize: 13, color: "#6b7280" }} />
                )}
                <Typography className={classes.iframeHeaderLabel}>
                  {isPreviewing ? "AI Modified Preview" : "Current Report"}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setIframeVisible((v) => !v)}
                startIcon={
                  iframeVisible ? (
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />
                  )
                }
                className={classes.iframeToggleBtn}
              >
                {iframeVisible ? "Hide" : "Show"}
              </Button>
            </Box>
            {iframeVisible && (
              <iframe
                key={iframeVersion}
                ref={mainIframeRef}
                className={classes.reportIframe}
                srcDoc={shownHtml}
                title="HTML Report"
                sandbox="allow-same-origin allow-scripts"
                onLoad={handleMainIframeLoad}
              />
            )}
          </Box>

          {/* ── Resizable divider ── */}
          {iframeVisible && (
            <Box
              className={`${classes.splitDivider}${isDragging ? ` ${classes.splitDividerActive}` : ""}`}
              onMouseDown={handleAiDividerMouseDown}
            />
          )}

          {/* ── Chat pane (right) ── */}
          <Box
            className={classes.aiChatPane}
            style={iframeVisible ? { width: `${100 - aiSplitRatio}%` } : { flex: 1 }}
          >
            {/* Message history */}
            <Box className={classes.aiChatHistory}>
              {chatHistory.length === 0 ? (
                <Box className={classes.chatEmptyState}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: "#c4b5fd", mb: 0.5 }} />
                  <Typography sx={{ fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                    Describe what to change — AI streams changes instantly.
                  </Typography>
                </Box>
              ) : (
                chatHistory.map((msg) => (
                  <ChatMessage key={msg.id} message={msg} />
                ))
              )}
              <div ref={chatBottomRef} />
            </Box>

            <Box className={classes.chatInputDivider} />

            {/* Attached files */}
            {attachedFiles.length > 0 && (
              <Box className={classes.fileRow}>
                {attachedFiles.map((f, i) => (
                  <FileChip key={i} file={f} onRemove={() => removeFile(i)} />
                ))}
              </Box>
            )}

            {/* Input row */}
            <Box className={classes.inputRow}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <Tooltip title="Attach documents or images">
                <IconButton
                  size="small"
                  className={classes.attachBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={streaming}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <TextField
                className={classes.promptInput}
                placeholder="Describe changes… (Ctrl+Enter to send)"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                maxRows={4}
                size="small"
                disabled={streaming}
                fullWidth
              />
              <Tooltip title={streaming ? "Streaming…" : "Send (Ctrl+Enter)"}>
                <span>
                  <IconButton
                    className={classes.sendBtn}
                    onClick={handleSend}
                    disabled={streaming || !hasPromptContent}
                  >
                    {streaming ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography className={classes.inputHint}>
              Ctrl+Enter to send · Attach files with the clip icon
            </Typography>
          </Box>
        </Box>
      ) : (
        /* ── Normal view mode: full-width iframe ── */
        <Box className={classes.iframeWrapper}>
          <iframe
            key={iframeVersion}
            ref={mainIframeRef}
            className={classes.reportIframe}
            srcDoc={shownHtml}
            title="HTML Report"
            sandbox="allow-same-origin allow-scripts"
            onLoad={handleMainIframeLoad}
          />
        </Box>
      )}

      {/* ── Save dialog ── */}
      <Dialog
        open={saveDialog}
        onClose={() => !saving && setSaveDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            How would you like to save the modified report?
          </Typography>
          <Box mt={1.5} display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Update Existing</strong> — replaces the current report with
              the modified version.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Save as New</strong> — creates a new entry in the history
              table alongside the original.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setSaveDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveNewConfirm}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : "Save as New"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveUpdateConfirm}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : "Update Existing"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
